import { useEffect } from "react";

/**
 * Freezes background scrolling while an overlay is open, without moving the page.
 *
 * The obvious approach — `overflow: hidden` on <html> — collapses the root
 * scrollport, and the browser drops the page to the top. Setting `scrollTop`
 * afterwards does not help: while overflow is hidden the assignment is clamped
 * to 0. That is what made popups reopen at the top of the page and left
 * fixed-position dropdown panels stranded away from their trigger.
 *
 * Instead the body is pinned with `position: fixed` and offset by the current
 * scroll, so the page looks identical while being unable to scroll. Releasing
 * restores the offset with a real `scrollTo`.
 *
 * Layout note: only the admin shell scrolls the document — the mentor shell
 * scrolls an inner container (`app.module.css .rootContent`), where the saved
 * offset is 0 and this is a no-op. Locking that inner container is a separate
 * concern and is NOT handled here.
 *
 * Locks are reference-counted because they nest: a Dropdown opened inside a
 * Popup must not release the Popup's lock when it closes.
 */

let lockCount = 0;
let savedScrollY = 0;
let savedStyles = null;

const applyLock = () => {
  const { body } = document;
  savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
  savedStyles = {
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
  };

  body.style.position = "fixed";
  body.style.top = `-${savedScrollY}px`;
  body.style.left = "0";
  body.style.right = "0";
  body.style.width = "100%";
};

const releaseLock = () => {
  if (!savedStyles) return;
  Object.assign(document.body.style, savedStyles);
  savedStyles = null;
  // `auto` rather than smooth — this is a restore, not a navigation.
  window.scrollTo(0, savedScrollY);
};

export const lockBodyScroll = () => {
  if (lockCount === 0) applyLock();
  lockCount += 1;
};

export const unlockBodyScroll = () => {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) releaseLock();
};

/**
 * @param {boolean} active Whether the lock should be held right now.
 */
const useScrollLock = (active = true) => {
  useEffect(() => {
    if (!active) return undefined;
    lockBodyScroll();
    return unlockBodyScroll;
  }, [active]);
};

export default useScrollLock;
